import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { Menu, X, Bell, LogOut, Home } from "lucide-react";
import Logo from "@/components/Logo";
import { ThemeToggle, LanguageToggle } from "@/components/Toggles";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function DashboardShell({ nav, children, home }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showN, setShowN] = useState(false);

  const loadN = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadN(); const i = setInterval(loadN, 30000); return () => clearInterval(i); }, []);
  const unread = notifs.filter((n) => !n.read).length;
  const markRead = async () => { setShowN(!showN); if (!showN && unread) { await api.post("/notifications/read"); loadN(); } };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center px-5 border-b border-border"><Logo onClick={() => setOpen(false)} /></div>
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-4rem)]">
          <Link to="/" onClick={() => setOpen(false)} data-testid="side-back-home"
            className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors mb-2">
            <Home size={18} /> {home}
          </Link>
          {nav.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} data-testid={`side-${to.split("/").pop() || "home"}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
      </aside>
      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="h-16 glass sticky top-0 z-20 flex items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(true)} className="lg:hidden" data-testid="dash-menu"><Menu size={22} /></button>
            <Link to="/" data-testid="dash-home-mobile" aria-label={home} className="sm:hidden flex h-9 w-9 items-center justify-center rounded-md border border-border hover:border-primary"><Home size={18} /></Link>
            <Link to="/" className="hidden sm:block text-sm link-muted" data-testid="dash-home">← {home}</Link>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle /><ThemeToggle />
            <div className="relative">
              <button onClick={markRead} className="relative flex h-9 w-9 items-center justify-center rounded-md border border-border hover:border-primary" data-testid="notif-btn">
                <Bell size={17} />
                {unread > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center">{unread}</span>}
              </button>
              {showN && (
                <div className="absolute right-0 mt-2 w-80 surface p-2 shadow-xl max-h-96 overflow-y-auto z-50">
                  {notifs.length === 0 ? <p className="p-4 text-sm text-muted-foreground text-center">—</p> :
                    notifs.map((n) => (
                      <div key={n.id} className="p-3 rounded-md hover:bg-secondary text-sm">
                        <div className="font-semibold">{n.title}</div>
                        <div className="text-muted-foreground text-xs">{n.body}</div>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <button onClick={() => { logout(); navigate("/"); }} className="flex h-9 w-9 items-center justify-center rounded-md border border-border hover:border-primary" data-testid="dash-logout"><LogOut size={17} /></button>
          </div>
        </header>
        <main className="p-5 sm:p-8">{children}</main>
      </div>
    </div>
  );
}
